/**
 * 节点上下文解析层。
 *
 * 这里只负责根据 React Flow 的入边关系，把上游节点数据包装成上下文 payload。
 * 节点数据读取走 node-custom-data，连接能力判断走 node-registry，避免在这里直接处理 Yjs 存储细节。
 */
import { useEffect, useMemo, useState } from 'react'
import { type Edge, type Node, useNodeId, useStore } from '@xyflow/react'
import { useSnapshot } from 'valtio/react'
import {
  getNodeDefinition,
  type NodeContextKind,
  type NodeContextResource,
} from '@/shared/lib/node-registry'
import { canvasAssetsStore } from '@/entities/node/canvas-asset/model/canvas-assets-store'
import type { CanvasAsset } from '@/entities/node/canvas-asset/model/canvas-asset-types'
import { readAssetRefsValue } from '@/entities/node/canvas-asset/lib/node-asset-refs'
import {
  readNodeCustomDataSnapshot,
  subscribeNodeCustomDataChanges,
} from './node-custom-data'
import { readListNodeAssetRefs } from './list-node-operation'
import { readSlotEdgeContext } from './slot-edge-context'
import { canConnectSourceKindToTarget, getNodeOutputKind } from './node-connection'
import { orderContextPayloadsByRefs, readCurrentOpRefs } from './node-context-refs'

/**
 * 单条上游上下文。
 *
 * edgeId 表示真实 React Flow 边；contextId 表示可选的展示/排序项 ID，slot reference 会用它区分同边多格。
 * data 是 source node 的自定义数据快照，具体字段由业务 feature 自己解释。
 */
export interface NodeContextPayload<TData extends Record<string, unknown> = Record<string, unknown>> {
  contextId?: string
  kind: NodeContextKind
  edgeId: string
  nodeId: string
  nodeType: string
  title: string
  data: TData
  resource?: NodeContextResource
}

/**
 * 一个目标节点当前能读取到的全部上游上下文。
 */
export interface NodeContextPayloads {
  items: NodeContextPayload[]
  byKind: Partial<Record<NodeContextKind, NodeContextPayload[]>>
}

function emptyNodeContextPayloads(): NodeContextPayloads {
  return { items: [], byKind: {} }
}

function groupNodeContextPayloads(items: NodeContextPayload[]): NodeContextPayloads {
  const byKind: Partial<Record<NodeContextKind, NodeContextPayload[]>> = {}
  items.forEach((payload) => {
    // byKind 只做基础分类，具体如何使用上下文由目标节点业务决定。
    ;(byKind[payload.kind] ??= []).push(payload)
  })

  return { items, byKind }
}

/**
 * 读取字符串字段。当前只用于从节点 data 中取 title。
 */
function stringField(
  data: Record<string, unknown>,
  key: string,
) {
  const value = data[key]
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 解析上下文展示标题。
 *
 * 优先使用节点数据里的 title，没有 title 时退回 nodeType，避免 payload 标题为空。
 */
function resolveNodeTitle(
  nodeType: string,
  nodeData: Record<string, unknown>,
) {
  return stringField(nodeData, 'title') || nodeType
}

type ReadCanvasAsset = (assetId: string) => CanvasAsset | undefined

/**
 * 从节点数据的 assetRefs 中解析上下文资源。
 *
 * @param nodeData source 节点自定义数据快照
 * @param readAsset 按 assetId 读取画布素材的函数
 * @returns 可供 OP payload 和上下文预览使用的资源信息；无 assetRefs 或素材缺失时返回 undefined
 */
function resolveNodeContextResource(
  nodeData: Record<string, unknown>,
  readAsset: ReadCanvasAsset,
): NodeContextResource | undefined {
  const assetId = readAssetRefsValue(nodeData.assetRefs)[0]
  if (!assetId) {
    return undefined
  }

  const asset = readAsset(assetId)
  if (!asset) {
    return undefined
  }

  return {
    assetId,
    type: asset.type,
    url: asset.url,
    previewUrl: asset.previewUrl,
    width: asset.width,
    height: asset.height,
    duration: asset.duration,
    check_status: asset.check_status,
  }
}

/**
 * 从当前画布素材 Store 中读取素材。
 *
 * @param assetId 画布素材 ID
 * @returns 当前 assetId 对应的 CanvasAsset；不存在时返回 undefined
 */
function readCanvasAssetFromStore(assetId: string) {
  return canvasAssetsStore.assets.get(assetId)
}

/**
 * 把 gallery reference edge 的 slotIndexes 动态解析成图片上下文。
 *
 * @param edge 入边，必须携带 mode: reference 的 slot context
 * @param nodeData source gallery 的数据快照
 * @param nodeId source gallery 节点 ID
 * @param nodeType source 节点类型，第一版只处理 gallery
 * @param readAsset 画布素材读取函数
 * @returns 当前批次中每个 slot 对应的图片 payload；空 slot 也会保留，用于 OP 引用条显示占位和支持单格删除
 */
function resolveSlotReferenceContextPayloads({
  edge,
  nodeData,
  nodeId,
  nodeType,
  readAsset,
}: {
  edge: Edge
  nodeData: Record<string, unknown>
  nodeId: string
  nodeType: string
  readAsset: ReadCanvasAsset
}): NodeContextPayload[] {
  const context = readSlotEdgeContext(edge)
  if (context?.mode !== 'reference') {
    return []
  }

  if (nodeType !== 'gallery' && nodeType !== 'text-list') {
    return []
  }

  // gallery和text-list都使用了相同的assetRefs结构，因此复用readGalleryAssetRefs读取
  const assetRefs = readListNodeAssetRefs(nodeData, false)
  const title = resolveNodeTitle(nodeType, nodeData)

  return context.slotIndexes.reduce<NodeContextPayload[]>((payloads, slotIndex) => {
    const assetId = assetRefs[slotIndex]
    const asset = assetId ? readAsset(assetId) : undefined
    const resource = asset
      ? {
          assetId,
          type: asset.type,
          url: asset.url,
          previewUrl: asset.previewUrl,
          width: asset.width,
          height: asset.height,
          duration: asset.duration,
          check_status: asset.check_status,
        }
      : undefined

    const fallbackKind = nodeType === 'text-list' ? 'text' : 'image'
    payloads.push({
      contextId: `${edge.id}:slot:${slotIndex}`,
      kind: resource?.type ?? fallbackKind,
      edgeId: edge.id,
      nodeId,
      // reference 解析后的上下文本质是具体资源类型
      nodeType: resource?.type ?? fallbackKind,
      title: `${title} 第 ${slotIndex + 1} 项`,
      data: {
        ...nodeData,
        assetRefs: assetId ? [assetId] : [],
        slotIndex,
        sourceNodeType: nodeType,
        content: asset?.type === 'text' ? asset.content : undefined,
      },
      ...(resource ? { resource } : {}),
    })

    return payloads
  }, [])
}

/**
 * 把一个 source node 转成上下文 payload。
 *
 * 这里只根据节点定义确认它是否有 output，以及 output 提供的 kind。
 * text/image/video/audio 等业务字段不在这里枚举，统一保留在 data 里。
 */
export function resolveNodeContextPayload({
  edgeId,
  nodeData,
  nodeId,
  nodeType,
  readAsset = readCanvasAssetFromStore,
}: {
  edgeId: string
  nodeData: Record<string, unknown>
  nodeId: string
  nodeType: string
  readAsset?: ReadCanvasAsset
}): NodeContextPayload | null {
  const definition = getNodeDefinition(nodeType)
  const outputKind = definition ? getNodeOutputKind(definition) : null
  if (!outputKind) {
    // 没有 output 的节点不能作为上下文来源。
    return null
  }

  return {
    kind: outputKind,
    edgeId,
    nodeId,
    nodeType,
    title: resolveNodeTitle(nodeType, nodeData),
    data: nodeData,
    resource: resolveNodeContextResource(nodeData, readAsset),
  }
}

export function resolveNodeContextPayloadsForNode({
  edges,
  nodeId,
  nodes,
  readAsset = readCanvasAssetFromStore,
}: {
  edges: Edge[]
  nodeId?: string
  nodes: Node[]
  readAsset?: ReadCanvasAsset
}): NodeContextPayloads {
  if (!nodeId) {
    return emptyNodeContextPayloads()
  }

  const targetNode = nodes.find((node) => node.id === nodeId)
  if (!targetNode) {
    return emptyNodeContextPayloads()
  }

  const incomingEdges = edges.filter((edge) => edge.target === nodeId)
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const items = incomingEdges
    .flatMap((edge) => {
      const sourceNode = nodeById.get(edge.source)
      if (!sourceNode?.type) return []
      const sourceNodeData = readNodeCustomDataSnapshot(sourceNode.id)

      const slotPayloads = resolveSlotReferenceContextPayloads({
        edge,
        nodeData: sourceNodeData,
        nodeId: sourceNode.id,
        nodeType: sourceNode.type,
        readAsset,
      })
      if (slotPayloads.length > 0 || readSlotEdgeContext(edge)?.mode === 'reference') {
        return slotPayloads
      }

      const payload = resolveNodeContextPayload({
        edgeId: edge.id,
        nodeData: sourceNodeData,
        nodeId: sourceNode.id,
        nodeType: sourceNode.type,
        readAsset,
      })
      return payload ? [payload] : []
    })
    .filter((payload): payload is NodeContextPayload => {
      return Boolean(
        payload &&
        canConnectSourceKindToTarget({
          nodes,
          sourceKind: payload.kind,
          target: nodeId,
        })
      )
    })

  const targetNodeData = readNodeCustomDataSnapshot(nodeId)
  const currentOpRefs = readCurrentOpRefs(targetNodeData)
  return groupNodeContextPayloads(orderContextPayloadsByRefs(items, currentOpRefs))
}

/**
 * 读取目标节点的上游上下文列表。
 *
 * 默认读取当前 React Flow 节点，也可以传 nodeId 读取指定节点。
 */
export function useNodeContextPayloads(nodeId?: string): NodeContextPayloads {
  const currentNodeId = useNodeId()
  const targetNodeId = nodeId ?? currentNodeId
  const nodes = useStore((state) => state.nodes)
  const edges = useStore((state) => state.edges)
  const assets = useSnapshot(canvasAssetsStore.assets)
  const [nodeDataVersion, setNodeDataVersion] = useState(0)

  const incomingEdges = useMemo(() => {
    if (!targetNodeId) {
      return []
    }

    return edges.filter((edge) => edge.target === targetNodeId)
  }, [edges, targetNodeId])

  const incomingSourceIds = useMemo(
    () => Array.from(new Set(incomingEdges.map((edge) => edge.source))),
    [incomingEdges],
  )

  useEffect(() => {
    // 上下文是派生数据。只监听当前入边 source 节点，避免无关节点变化触发重算。
    return subscribeNodeCustomDataChanges(incomingSourceIds, () => {
      setNodeDataVersion((version) => version + 1)
    })
  }, [incomingSourceIds])

  return useMemo(() => {
    // nodeDataVersion 只用于让 source 节点数据变更时重新解析上下文 payload。
    void nodeDataVersion

    if (!targetNodeId) {
      return emptyNodeContextPayloads()
    }

    return resolveNodeContextPayloadsForNode({
      edges: incomingEdges,
      nodeId: targetNodeId,
      nodes,
      readAsset: (assetId) => assets.get(assetId) as CanvasAsset | undefined,
    })
  }, [assets, incomingEdges, nodeDataVersion, nodes, targetNodeId])
}
